import type { AIOwnerBrief, AIOwnerBriefSuggestion } from '@/types/ai'

// ── Pure derivation of the dashboard brief from the stored metrics ──────────
// The backend stores the numbers (ai_digests.metrics) and renders the bubble's
// text from them; the dashboard renders the same numbers as rows so they can
// carry deltas and links. Kept free of React so the wording is unit-tested.

interface Cmp {
  value: number
  previous?: number | null
}

export interface BriefHeadline {
  key: 'net_sales' | 'orders' | 'net_profit' | 'operating_expenses'
  label: string
  value: number
  /** Change vs the previous closed day; null when there is no earlier day. */
  delta: number | null
  deltaPct: number | null
  /** Whether a rise is good news (sales) or bad (expenses). */
  goodWhenUp: boolean
  isMoney: boolean
}

export interface BriefFlag {
  kind: string
  label: string
  prompt: string
  target?: string
  /** The number behind the finding: a count (low_stock) or a money amount (cash_variance, refunds). */
  count?: number
  amount?: number
}

export interface BriefRows {
  businessDate: string
  comparedTo: string | null
  headlines: BriefHeadline[]
  /** One row per finding, in the backend's order. */
  flags: BriefFlag[]
  /** True when the brief carries no findings at all. */
  allClear: boolean
  /** Cost data is thin, so profit is not to be trusted. */
  lowCoverage: boolean
  negativeProfit: boolean
}

function num(v: unknown): number {
  return typeof v === 'number' && Number.isFinite(v) ? v : 0
}

function cmp(v: unknown): Cmp {
  if (typeof v !== 'object' || v === null) return { value: 0, previous: null }
  const o = v as Record<string, unknown>
  const previous = typeof o.previous === 'number' ? o.previous : null
  return { value: num(o.value), previous }
}

function headline(
  key: BriefHeadline['key'],
  label: string,
  c: Cmp,
  goodWhenUp: boolean,
  isMoney: boolean,
): BriefHeadline {
  const prev = c.previous ?? null
  const delta = prev === null ? null : c.value - prev
  let deltaPct: number | null = null
  if (delta !== null && prev !== null) {
    deltaPct = prev === 0 ? (delta === 0 ? 0 : null) : (delta / Math.abs(prev)) * 100
  }
  return { key, label, value: c.value, delta, deltaPct, goodWhenUp, isMoney }
}

function isSuggestion(v: unknown): v is AIOwnerBriefSuggestion {
  if (typeof v !== 'object' || v === null) return false
  const o = v as Record<string, unknown>
  return typeof o.label === 'string' && o.label !== '' && typeof o.prompt === 'string' && o.prompt !== ''
}

/** Rows for the dashboard brief, or null when the brief has no usable metrics. */
export function briefRows(brief: AIOwnerBrief | null | undefined): BriefRows | null {
  if (!brief || !brief.metrics || typeof brief.metrics !== 'object') return null
  const m = brief.metrics as Record<string, unknown>
  const netSales = cmp(m.net_sales)
  const headlines: BriefHeadline[] = [
    headline('net_sales', 'Sales', netSales, true, true),
    headline('orders', 'Orders', cmp(m.orders), true, false),
    headline('net_profit', 'Net profit', cmp(m.net_profit), true, true),
    headline('operating_expenses', 'Expenses', cmp(m.operating_expenses), false, true),
  ]
  const figures: Record<string, Partial<Pick<BriefFlag, 'count' | 'amount'>>> = {
    low_stock: { count: num(m.low_stock_items) },
    cash_variance: { amount: typeof m.cash_variance === 'number' ? m.cash_variance : undefined },
    refunds: { amount: num(m.refunds) },
    fiscal: { count: num(m.fiscal_dead_jobs) },
  }
  const flags: BriefFlag[] = (Array.isArray(m.suggestions) ? m.suggestions : [])
    .filter(isSuggestion)
    .map((s) => {
      const kind = typeof s.kind === 'string' ? s.kind : s.label
      return {
        kind,
        label: s.label,
        prompt: s.prompt,
        target: typeof s.target === 'string' && s.target !== '' ? s.target : undefined,
        ...(figures[kind] ?? {}),
      }
    })
  const comparedTo = typeof m.compared_to === 'string' && m.compared_to !== '' ? m.compared_to : null
  const coverage = num(m.cost_coverage_pct)
  return {
    businessDate: brief.business_date,
    comparedTo,
    headlines,
    flags,
    allClear: flags.length === 0,
    lowCoverage: netSales.value > 0 && coverage < 50,
    negativeProfit: headlines[2].value < 0,
  }
}

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** "Mon, 12 Oct" from an ISO date (same shape as the bubble's text); the raw string if it does not parse. */
export function briefDateLabel(iso: string): string {
  const [y, mo, d] = iso.split('-').map(Number)
  if (!y || !mo || !d || mo < 1 || mo > 12) return iso
  const date = new Date(Date.UTC(y, mo - 1, d))
  return `${WEEKDAYS[date.getUTCDay()]}, ${d} ${MONTHS[mo - 1]}`
}
